import { Components, Theme } from "@mui/material/styles";
// Side-effect import: merges MuiDataGrid into MUI's Components<Theme> type so
// the override below type-checks.
import type {} from "@mui/x-data-grid/themeAugmentation";
import { radius, shadow } from "./tokens";

// Component-level style overrides — this is the part that reaches almost
// every existing page for free: Button/TextField/Paper/AppBar/Drawer/Chip/
// Dialog/DataGrid all read from here without any per-page changes.
export const getComponents = (theme: Theme): Components<Theme> => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarColor:
          theme.palette.mode === "dark"
            ? `${theme.palette.grey[600]} transparent`
            : `${theme.palette.grey[300]} transparent`,
        // Consumed by global.css's ::-webkit-scrollbar-thumb so Chromium/
        // Safari track the same light/dark value Firefox gets above.
        "--scrollbar-thumb":
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.16)"
            : "rgba(43, 49, 65, 0.18)",
        "--scrollbar-thumb-hover":
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.28)"
            : "rgba(43, 49, 65, 0.32)",
      },
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundImage: "none",
        borderRadius: radius.lg,
      },
      elevation1: { boxShadow: shadow.sm },
      elevation2: { boxShadow: shadow.md },
      elevation3: { boxShadow: shadow.lg },
    },
  },
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        borderRadius: radius.lg,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "none",
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: radius.sm,
        paddingInline: "1.1rem",
        paddingBlock: "0.55rem",
      },
      containedPrimary: {
        "&:hover": { backgroundColor: theme.palette.primary.dark },
      },
      outlined: {
        borderColor: theme.palette.divider,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radius.sm,
        backgroundColor: theme.palette.background.paper,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.divider,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: "0.9rem" },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: radius.sm, fontWeight: 600 },
    },
  },
  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "none",
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderInlineEnd: `1px solid ${theme.palette.divider}`,
        borderInlineStart: "none",
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: radius.sm,
        marginInline: "0.5rem",
        marginBlock: "0.125rem",
        "&.Mui-selected": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(8, 167, 132, 0.16)"
              : "rgba(8, 167, 132, 0.1)",
          color: theme.palette.primary.main,
          "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(8, 167, 132, 0.22)"
                : "rgba(8, 167, 132, 0.16)",
          },
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: radius.lg, boxShadow: shadow.lg },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderColor: theme.palette.divider },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: "none",
        "--DataGrid-rowBorderColor": theme.palette.divider,
      },
      columnHeaders: {
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : theme.palette.grey[50],
        borderRadius: 0,
      },
      cell: { borderColor: theme.palette.divider },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: theme.palette.grey[900],
        borderRadius: radius.sm,
        fontSize: "0.75rem",
      },
    },
  },
});
